import { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, MessageCircle, Link2, Check } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StoreChooser from '../components/StoreChooser';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import { formatarPreco, emOferta, copiarTexto, PRODUTOS } from '../data/produtos';
import { fetchProduto } from '../data/api';

export default function ProdutoPage() {
  const { id } = useParams();
  const [seletorAberto, setSeletorAberto] = useState(false);
  const [fotoAtiva, setFotoAtiva] = useState(0);

  const fallback = PRODUTOS.find((p) => p.id === id);
  const { data: produto, isError, isLoading } = useQuery({
    queryKey: ['produto', id],
    queryFn: () => fetchProduto(id),
    retry: 1,
    placeholderData: fallback ? { ...fallback, fotos: [fallback.imagem], esgotado: false } : undefined,
  });

  if (!fallback && isError) return <Navigate to="/" replace />;
  if (isLoading && !produto) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-mk-blue border-t-transparent" />
      </div>
    );
  }

  const fotos = produto.fotos && produto.fotos.length > 0 ? produto.fotos : [produto.imagem];

  const copiarLink = async () => {
    const ok = await copiarTexto(window.location.href);
    if (ok) toast.success('Link do produto copiado!');
    else toast.error('Não foi possível copiar o link.');
  };

  return (
    <div data-testid="product-page" className="min-h-screen bg-white font-body text-slate-900 antialiased">
      <Header onWhatsAppGeral={() => setSeletorAberto(true)} />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link
          data-testid="product-page-back-link"
          to="/"
          className="inline-flex items-center gap-2 text-sm font-bold text-mk-blue transition-colors hover:text-mk-bluelight"
        >
          <ArrowLeft size={16} />
          Voltar ao catálogo
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 grid gap-6 sm:grid-cols-2 sm:gap-10"
        >
          <div>
            <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-slate-50">
              {produto.esgotado ? (
                <span className="absolute left-3 top-3 z-10 rounded-full bg-slate-700 px-3 py-1 text-[11px] font-extrabold uppercase tracking-widest text-white shadow-lg">
                  Esgotado
                </span>
              ) : (
                emOferta(produto) && (
                  <span className="absolute left-3 top-3 z-10 rounded-full bg-mk-red px-3 py-1 text-[11px] font-extrabold uppercase tracking-widest text-white shadow-lg">
                    Oferta
                  </span>
                )
              )}
              <img src={fotos[fotoAtiva]} alt={produto.nome} className="aspect-square w-full object-cover" />
            </div>
            {fotos.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                {fotos.map((foto, i) => (
                  <button
                    key={foto + i}
                    data-testid={`product-page-thumb-${i}`}
                    onClick={() => setFotoAtiva(i)}
                    aria-label={`Foto ${i + 1}`}
                    className={`h-16 w-16 shrink-0 overflow-hidden rounded-xl border-2 transition-colors ${i === fotoAtiva ? 'border-mk-blue' : 'border-slate-200 opacity-70 hover:opacity-100'}`}
                  >
                    <img src={foto} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col">
            {produto.marca && (
              <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-mk-blue">{produto.marca}</p>
            )}
            <h1 className="mt-1.5 font-heading text-2xl sm:text-3xl font-extrabold leading-tight text-slate-900">
              {produto.nome}
            </h1>

            <ul className="mt-4 space-y-2">
              {produto.detalhes.map((d) => (
                <li key={d} className="flex items-center gap-2 text-sm text-slate-600">
                  <Check size={15} className="shrink-0 text-mk-green" strokeWidth={3} />
                  {d}
                </li>
              ))}
            </ul>

            <p className="mt-4 text-sm sm:text-base leading-relaxed text-slate-500">{produto.descricao}</p>

            <div className="mt-5 rounded-2xl bg-slate-50 p-4">
              {produto.parcelas && (
                <p className="text-xs font-medium text-slate-500">
                  {produto.parcelas.vezes}x de {formatarPreco(produto.parcelas.valor)}
                </p>
              )}
              {produto.precoDe && (
                <p className="mt-1 text-sm font-medium text-slate-400 line-through">
                  de {formatarPreco(produto.precoDe)}
                </p>
              )}
              <p className="mt-1 flex items-baseline gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">à vista</span>
                <span data-testid="product-page-price" className="font-heading text-3xl font-black tracking-tight text-mk-red">
                  {formatarPreco(produto.preco)}
                </span>
              </p>
              {produto.esgotado && (
                <p data-testid="product-page-esgotado" className="mt-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                  Produto esgotado — consulte a disponibilidade pelo WhatsApp
                </p>
              )}
              {produto.estoqueExibido != null && !produto.esgotado && (
                <p data-testid="product-page-stock" className="mt-2 text-sm font-bold text-mk-blue">
                  {produto.estoqueExibido} unidades em estoque
                </p>
              )}
            </div>

            <div className="mt-5 flex flex-col gap-2.5">
              <button
                data-testid="product-page-whatsapp-button"
                onClick={() => setSeletorAberto(true)}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-mk-green px-5 py-3.5 text-sm sm:text-base font-bold text-white shadow-lg shadow-mk-green/30 transition-colors duration-300 hover:bg-mk-greendark"
              >
                <MessageCircle size={19} strokeWidth={2.5} />
                Consultar pelo WhatsApp
              </button>
              <button
                data-testid="product-page-copy-link-button"
                onClick={copiarLink}
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-mk-blue px-5 py-2.5 text-sm font-bold text-mk-blue transition-colors duration-300 hover:bg-mk-blue hover:text-white"
              >
                <Link2 size={16} />
                Copiar link do produto
              </button>
            </div>
          </div>
        </motion.div>
      </main>

      <Footer />
      <FloatingWhatsApp onClick={() => setSeletorAberto(true)} />

      <AnimatePresence>
        {seletorAberto && <StoreChooser produto={produto} onFechar={() => setSeletorAberto(false)} />}
      </AnimatePresence>
    </div>
  );
}
